import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PopupFullWidthOnMobile } from '@components/molecules/PopupFullWidthOnMobile';
import { Form } from '@components/atoms/Form';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType, SimpleOptionsList } from '@components/atoms/Select';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { InputErrors, OrderDelivery } from '@types';

interface EditDeliveryFormErrors {
    address: InputErrors;
    options: InputErrors;
}

interface EditDeliveryModalProps {
    deliveryId: string | null;
    deliveries: OrderDelivery[];
    allDeliveryOptions: SimpleOptionsList;
    show: boolean;
    onClose: () => void;
}

export const EditDeliveryModal: FC<EditDeliveryModalProps> = ({ deliveryId, deliveries, allDeliveryOptions, show, onClose }) => {
    const { t } = useTranslation();

    const translations = {
        title: t('edit-delivery-modal-component.title'),
        addressLabel: t('edit-delivery-modal-component.address.label'),
        optionsLabel: t('edit-delivery-modal-component.options.label'),
        selectOptionsLabel: t('select-default-option'),
        saveButton: t('edit-delivery-modal-component.save.button'),
        addressRequiredError: t('edit-delivery-modal-component.address.required-error'),
        optionRequiredError: t('edit-delivery-modal-component.options.required-error'),
    };

    const delivery = deliveries.find((delivery) => delivery.id === deliveryId);

    const formRef = useRef<HTMLFormElement>(null);
    const [fieldAddress, setFieldAddress] = useState<string>(delivery?.address || '');
    const [selectedOption, setSelectedOption] = useState<string | null>(delivery?.option.value || null);

    // when deliveryId changes, update values for modal
    useEffect(() => {
        if (show) {
            setFieldAddress(delivery?.address || '');
            setSelectedOption(delivery?.option.value || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<EditDeliveryFormErrors>({
        address: [],
        options: [],
    });

    const handleOnClickSave = () => {
        const errors = validateForm();

        if (errors.address.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => EditDeliveryFormErrors = () => {
        const newErrors: EditDeliveryFormErrors = {
            address: [],
            options: [],
        };

        // address is required
        !fieldAddress.trim() && newErrors.address.push(translations.addressRequiredError);
        !selectedOption && newErrors.options.push(translations.optionRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = () => {
        /* #TODO */
        // console.log('data to submit', { fieldAddress, selectedOption });
        alert('submit form');
        onClose();
    };

    return (
        <PopupFullWidthOnMobile title={translations.title} show={show} onHide={() => onClose()}>
            <Form formRef={formRef} onSubmit={submitForm}>
                <div className="d-flex flex-column gap-3">
                    {/* address */}
                    <Input
                        type={InputTypes.textarea}
                        label={translations.addressLabel}
                        variant={InputVariants.labeledInput}
                        value={fieldAddress}
                        onChange={(value) => setFieldAddress(value)}
                        state={errors.address.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.address}
                    />
                    <div>
                        <Typography color="muted" fontSize="12">
                            {translations.optionsLabel}
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            options={
                                deliveryId
                                    ? allDeliveryOptions
                                    : [{ value: '', text: translations.selectOptionsLabel }, ...allDeliveryOptions]
                            }
                            value={selectedOption || undefined}
                            onChange={(e) => setSelectedOption(e)}
                        />
                        {errors.options.length > 0 && <ErrorMessages messages={errors.options} />}
                    </div>

                    <Button
                        label={translations.saveButton}
                        classes="w-100"
                        mode={ButtonMode.primary}
                        onClick={handleOnClickSave}
                    />
                </div>
            </Form>
        </PopupFullWidthOnMobile>
    );
};
